/** Angular imports */
import {
    Component,
    DestroyRef,
    effect,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Third Party imports */
import {
    DuplicateResult,
    RenamedFileEvent,
    UploadConfig,
    Uploader,
    UploadFile,
} from '@ntv360/component-pantry';
import {
    catchError,
    concatMap,
    debounceTime,
    EMPTY,
    map,
    Observable,
    of,
    Subject,
    Subscription,
    switchMap,
    takeUntil,
    tap,
    toArray,
} from 'rxjs';

/** Core imports */
import { EnvironmentService, FilestackUploadService, MediaLibraryService } from '@core';
import { SuggestNameResponse } from '@core/services/media-library/media-library.interface';

/** Local imports */
import { BaseQueryParams } from '@shared';
import { getUploaderConfig } from '@shared/constants';
import {
    fetchAllBatch,
    generateFilenameSuggestion,
    getMediaType,
    withLoading,
} from '@shared/utils';

/**
 * A wrapper component that integrates the `@ntv360/component-pantry` UI uploader
 * with the application's `FilestackUploadService`.
 *
 * This component acts as the bridge between the UI presentation and the cloud upload infrastructure.
 * Key responsibilities include:
 * - Managing transient file selection state and the uploader modal visibility.
 * - Preventing file name collisions by generating valid, auto-incrementing suffix names (e.g., `filename(1)`) for duplicate uploads.
 * - Delegating the physical file upload stream to Filestack and feeding percentage updates back to the UI.
 * - Safely handling mid-flight upload cancellations.
 * - Emitting an `onUploadSuccess` event per successfully uploaded file, allowing the parent coordinator to batch saves.
 */
@Component({
    selector: 'app-uploader',
    imports: [Uploader],
    templateUrl: './uploader.component.html',
    styleUrl: './uploader.component.scss',
})
export class UploaderComponent {
    /** Services in managing upload into filestack */
    private filestackService = inject(FilestackUploadService);

    /** Service for managing medias/content data and API operations. Used to check for existing media files in the database */
    private mediaLibraryService = inject(MediaLibraryService);

    /** Cleans up in-flight duplicate checks when the component is destroyed */
    private destroyRef = inject(DestroyRef);

    /** Runtime environment configuration */
    private env = inject(EnvironmentService);

    /** Default upload configuration computed from runtime environment */
    protected defaultUploadConfig = getUploaderConfig(this.env);

    /** Uploader component reference */
    private uploaderComponent = viewChild<Uploader>('uploader');

    /**
     * Emits the latest selection from the pantry uploader. Pantry fires `filesSelected`
     * once per file as a multi-file drop is processed, not once for the whole batch —
     * debounced + switchMap'd so a burst of N file additions runs one full duplicate
     * check against the settled batch, instead of one (increasingly expensive) check
     * per intermediate partial state.
     */
    private filesSelected$ = new Subject<UploadFile[]>();

    /** Immediately cancels an in-flight duplicate check; the debounce on `filesSelected$`
     * means a superseding emission doesn't switchMap-cancel fast enough for a close
     * action, which needs the in-flight request stopped right away. */
    private cancelDuplicateCheck$ = new Subject<void>();

    /** Active rename-recheck subscription; cancelled if the same file is renamed again */
    private renameCheckSub?: Subscription;

    /** Required: Controls the visibility of the uploader */
    public showUploader = model.required<boolean>();

    /** Optional: Configuration for the uploader. Defaults to the runtime config from EnvironmentService */
    public uploadConfig = input<UploadConfig>();

    /** Optional: Part of the uploader configuration. Controls the maximum number of files to upload. Defaults to `20` */
    public maxFilesToUpload = input<number>(20);

    /** Default optimize toggle for newly selected video files */
    public defaultOptimizeVideo = input<boolean>(true);

    /** Optional dealer scope for duplicate checks against the media library */
    public dealerId = input<string | null>(null);

    /** Holds the duplicate selected files based on filenames. Used by the uploader component */
    protected duplicateFiles = signal<DuplicateResult[]>([]);

    /** Allows us temporarily disable the  uploader component while checking for duplicate files */
    protected isCheckingDuplicates = signal<boolean>(false);

    /** Holds the current batch's resolved duplicate results, keyed by file id */
    private resolvedDuplicates = new Map<string, DuplicateResult>();

    /**
     * The final resolved name (stem) claimed by each already-checked file, keyed by
     * file id. Persists across selection changes so a file only needs checking once —
     * its resolution is stable (later files avoid its claimed name, never the reverse)
     * — instead of re-verifying the whole batch against the backend every time a new
     * file is added.
     */
    private claimedStems = new Map<string, string>();

    /** Tracks video files that already received the initial optimize default */
    private videoDefaultsApplied = new Set<string>();

    /**
     * Filenames already present in the target dealer's (or floating) media library.
     * Fed to the pantry uploader so its inline rename flow can catch collisions
     * locally as a fast pre-check; `onFileRenamed` re-validates against the
     * backend on top of this.
     */
    protected existingLibraryNames = signal<Set<string>>(new Set());

    /** Active existing-library-names fetch; cancelled when the uploader re-opens */
    private existingNamesSub?: Subscription;

    /** Emits when the uploader modal is closed */
    public closeUploader = output<void>();

    /** Emits everytime a successful upload happened */
    public onUploadSuccess = output<{
        file: UploadFile & { url: string; s3url?: string };
        cb: (fileId: string, url: string) => void;
    }>();

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.existingNamesSub?.unsubscribe();
            this.renameCheckSub?.unsubscribe();
        });

        // Refresh the known library filenames whenever the uploader opens or the dealer scope changes
        effect(() => {
            const isOpen = this.showUploader();
            const dealerId = this.dealerId();
            if (isOpen) {
                this.loadExistingLibraryNames(dealerId);
            }
        });

        // Debounce bursts of filesSelected emissions (one per file during a multi-file
        // drop) into a single check against the settled batch; switchMap cancels any
        // still-in-flight check from a now-superseded selection.
        this.filesSelected$
            .pipe(
                debounceTime(200),
                switchMap((files) => this.checkFilesForDuplicates(files)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /** Holds the funciton reference to be called to cancel ongoing upload */
    private cancelUpload?: () => void;

    /**
     * Handles the closure of the uploader modal by resetting all transient state.
     * * This method performs the following cleanup:
     * 1. Closes the UI overlay.
     * 2. Clears the list of identified duplicate files.
     * 3. Resets collision tracking (Sets and Maps) to ensure a fresh state for the next session.
     * 4. Notifies the parent component that the uploader has been dismissed.
     */
    protected onUploaderClose(): void {
        // Stops any in-flight duplicate check immediately — filesSelected$ is debounced,
        // so a superseding emission wouldn't switchMap-cancel it fast enough here.
        this.cancelDuplicateCheck$.next();
        this.existingNamesSub?.unsubscribe();
        this.renameCheckSub?.unsubscribe();
        this.showUploader.set(false);
        this.duplicateFiles.set([]);
        this.isCheckingDuplicates.set(false);
        this.resolvedDuplicates.clear();
        this.claimedStems.clear();
        this.videoDefaultsApplied.clear();
        this.existingLibraryNames.set(new Set());
        this.closeUploader.emit();
    }

    /**
     * Fetches every filename currently in scope for the given dealer (or the
     * floating library when `dealerId` is null) so the pantry uploader's rename
     * flow can locally reject collisions.
     *
     * @param dealerId - Dealer to scope the fetch to, or null for floating content.
     */
    private loadExistingLibraryNames(dealerId: string | null): void {
        this.existingNamesSub?.unsubscribe();

        const fetcher = dealerId
            ? (params: BaseQueryParams) =>
                  this.mediaLibraryService.getDealerContents(dealerId, params)
            : (params: BaseQueryParams) =>
                  this.mediaLibraryService.getFloatingContents(null, params);

        this.existingNamesSub = fetchAllBatch(fetcher, {})
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((contents) => {
                this.existingLibraryNames.set(new Set(contents.map((c) => c.fileName)));
            });
    }

    /**
     * Normalizes a mime type for jpg files. Browsers always report `.jpg` and
     * `.jpeg` files with the identical mime type `image/jpeg`, but the backend
     * distinguishes them — files stored as `.jpg` are recorded as `image/jpg`.
     * Without this, a `.jpg` upload's duplicate check (and the final persisted
     * record) disagree on type with an existing `.jpg` file, and the collision
     * is missed entirely.
     *
     * @param mimeType - The file's browser-reported mime type.
     * @param extension - The file's extension.
     * @returns The mime type to use for both duplicate checks and persistence.
     */
    private normalizeFileType(mimeType: string, extension?: string): string {
        return mimeType === 'image/jpeg' && extension?.toLowerCase() === 'jpg'
            ? 'image/jpg'
            : mimeType;
    }

    /**
     * Checks a candidate filename against the backend `suggest-name` endpoint,
     * scoped to the current dealer. A conflict means the backend proposed a
     * different filename — i.e. the requested name already exists.
     *
     * @param name - The candidate filename (without awaiting normalization).
     * @param mimeType - The file's mime type.
     * @param extension - The file's extension, used to compare stems consistently.
     */
    private checkNameConflict(
        name: string,
        mimeType: string,
        extension?: string,
    ): Observable<{ hasConflict: boolean; response: SuggestNameResponse | null }> {
        const searchStem = this.getComparableStem(name, extension);
        const normalizedMimeType = this.normalizeFileType(mimeType, extension);

        return this.mediaLibraryService.suggestName(name, normalizedMimeType, this.dealerId()).pipe(
            map((response) => {
                const suggestedStem = this.getComparableStem(response.suggestedFileName, extension);
                return { hasConflict: suggestedStem !== searchStem, response };
            }),
            catchError(() => of({ hasConflict: false, response: null })),
        );
    }

    /**
     * Resolves a name for a candidate that is free of both backend conflicts and
     * collisions with files already claimed earlier in this same batch. The backend
     * only knows about persisted library content — it has no visibility into sibling
     * files still sitting unsaved locally in the same upload batch — so two files
     * checked independently can otherwise both be told the same "next available" name.
     *
     * @param searchName - The candidate filename to check.
     * @param mimeType - The file's mime type.
     * @param extension - The file's extension.
     * @param claimedNames - Stems already spoken for by earlier files in this batch; mutated as names are claimed.
     */
    private resolveConflictFreeName(
        searchName: string,
        mimeType: string,
        extension: string | undefined,
        claimedNames: Set<string>,
    ): Observable<{ hasConflict: boolean; suggestedTitle: string; finalStem: string }> {
        const originalStem = this.getComparableStem(searchName, extension);
        return this.resolveConflictFreeNameAttempt(
            originalStem,
            searchName,
            mimeType,
            extension,
            claimedNames,
            0,
        );
    }

    /**
     * Recursive attempt loop backing {@link resolveConflictFreeName}. Re-verifies
     * against the backend each time the candidate name is bumped, so a locally
     * disambiguated suggestion is never handed out without confirming the backend
     * also considers it free.
     *
     * @param originalStem - Comparable stem of the name originally requested; used to compute `hasConflict` relative to the caller's intent, not the current attempt.
     * @param attemptName - The name being checked on this attempt (bumped on each retry).
     * @param mimeType - The file's mime type.
     * @param extension - The file's extension.
     * @param claimedNames - Stems already spoken for by earlier files in this batch; mutated as names are claimed.
     * @param depth - Current retry count; caps recursion so a persistently broken backend response can't loop forever.
     */
    private resolveConflictFreeNameAttempt(
        originalStem: string,
        attemptName: string,
        mimeType: string,
        extension: string | undefined,
        claimedNames: Set<string>,
        depth: number,
    ): Observable<{ hasConflict: boolean; suggestedTitle: string; finalStem: string }> {
        const attemptStem = this.getComparableStem(attemptName, extension);

        return this.checkNameConflict(attemptName, mimeType, extension).pipe(
            switchMap(({ hasConflict, response }) => {
                const candidateTitle = hasConflict && response ? response.suggestedTitle : attemptName;
                const candidateStem = this.getComparableStem(
                    hasConflict && response ? response.suggestedFileName : attemptName,
                    extension,
                );
                // Extension-scoped: same stem, different extension is not a collision
                // (e.g. test_duplicate.png vs test_duplicate.mp4 are different files).
                const claimKey = this.getClaimKey(candidateStem, extension);

                // The backend can report a conflict but echo back the same name it was
                // just asked about (an increment bug on its side) — a self-referential
                // "suggestion" that doesn't resolve anything. Treat that the same as a
                // batch-sibling collision: bump locally and re-verify, bounded so a
                // persistently broken backend response can't loop forever.
                const backendDidNotIncrement = hasConflict && candidateStem === attemptStem;
                if ((backendDidNotIncrement || claimedNames.has(claimKey)) && depth < 25) {
                    return this.resolveConflictFreeNameAttempt(
                        originalStem,
                        this.bumpNameSuffix(candidateTitle),
                        mimeType,
                        extension,
                        claimedNames,
                        depth + 1,
                    );
                }

                claimedNames.add(claimKey);
                return of({
                    hasConflict: candidateStem !== originalStem,
                    suggestedTitle: candidateTitle,
                    finalStem: claimKey,
                });
            }),
        );
    }

    /**
     * Increments (or adds) a trailing numeric suffix on a filename stem, used to
     * locally disambiguate batch-sibling collisions the backend can't see.
     */
    private bumpNameSuffix(stem: string): string {
        const match = stem.match(/^(.*)_(\d+)$/);
        return match ? `${match[1]}_${Number(match[2]) + 1}` : `${stem}_2`;
    }

    /**
     * Handles newly selected files (from selection or drag and drop). Video-optimize
     * defaults are applied immediately; the duplicate check itself is pushed through
     * `filesSelected$` (debounced) since pantry fires this once per file during a
     * multi-file drop, not once for the whole batch.
     *
     * @param files - The current full selection to evaluate for duplicates.
     */
    protected onFilesSelected(files: UploadFile[]): void {
        const newVideoFileIds = files.filter((f) => getMediaType(f) === 'video').map((f) => f.id);
        this.applyVideoOptimizationDefaults(newVideoFileIds);

        // Pantry auto-generates its own naive local suggestion (e.g. "_1") for any newly
        // flagged duplicate the instant it's selected, purely from `existingLibraryNames`
        // — well before our real, backend-driven suggestion is ready. Its suggestion panel
        // falls back to this field whenever our `duplicateResults` has no match yet, so
        // clearing our own signal isn't enough; clear the field on the file objects
        // themselves so nothing confusing renders during the check. (Blocked/invalid-name
        // files are left alone — their `suggestedRename` is a sanitized name, not a
        // duplicate-numbering guess, and isn't part of this race.)
        const pendingDuplicateIds = new Set(
            files.filter((f) => f.status !== 'uploaded' && f.status !== 'blocked').map((f) => f.id),
        );
        this.uploaderComponent()?.selectedFiles.update((current) =>
            current.map((f) =>
                pendingDuplicateIds.has(f.id) && f.suggestedRename
                    ? { ...f, suggestedRename: undefined }
                    : f,
            ),
        );

        this.filesSelected$.next(files);
    }

    /**
     * Asks the backend `suggest-name` endpoint for a non-conflicting name per pending
     * file, scoped to the current dealer. A file is flagged as a duplicate only when
     * the resolved name differs from the name we sent — meaning the name already
     * exists (in the library, or claimed by a sibling elsewhere in this same batch).
     * Renames are re-validated the same way via `onFileRenamed`.
     *
     * @param files - The full current selection (from a settled `filesSelected$` emission).
     */
    private checkFilesForDuplicates(files: UploadFile[]): Observable<unknown> {
        // Already-uploaded files stay in the pantry uploader's selection list (only their
        // status flips to 'uploaded'), so they keep showing up here on every subsequent
        // selection within the same open session. Exclude them so a completed file can't
        // resurface as a stale duplicate suggestion.
        const pendingFiles = files.filter((f) => f.status !== 'uploaded');

        if (pendingFiles.length === 0) {
            this.duplicateFiles.set([]);
            this.resolvedDuplicates.clear();
            this.claimedStems.clear();
            this.videoDefaultsApplied.clear();
            return EMPTY;
        }

        // Drop tracking for files no longer selected (removed, or since uploaded).
        const pendingIds = new Set(pendingFiles.map((f) => f.id));
        let prunedAny = false;
        for (const id of [...this.claimedStems.keys()]) {
            if (!pendingIds.has(id)) {
                this.claimedStems.delete(id);
                this.resolvedDuplicates.delete(id);
                prunedAny = true;
            }
        }
        if (prunedAny) this.duplicateFiles.set([...this.resolvedDuplicates.values()]);

        // Only check files not already resolved by a previous pass. A file's resolution
        // is stable once made — later files avoid its claimed name, never the reverse —
        // so re-verifying it again against the backend on every subsequent selection
        // change is unnecessary, and gets expensive fast as the batch grows.
        const filesToCheck = pendingFiles.filter((f) => !this.claimedStems.has(f.id));
        if (filesToCheck.length === 0) return EMPTY;

        const claimedNames = new Set(this.claimedStems.values());

        // Processed one at a time (not parallel) so each file's claim is registered
        // before the next file is checked against it.
        const checks$ = filesToCheck.map((f) => {
            const isBlocked = f.status === 'blocked';
            const searchName = isBlocked
                ? generateFilenameSuggestion(f.currentName)
                : this.getUploadFileStem(f);

            return () =>
                this.resolveConflictFreeName(searchName, f.mimeType, f.extension, claimedNames).pipe(
                    map(({ hasConflict, suggestedTitle, finalStem }) => ({
                        file: f,
                        isBlocked,
                        hasConflict,
                        suggestedTitle,
                        finalStem,
                    })),
                );
        });

        return of(...checks$).pipe(
            concatMap((check) => check()),
            toArray(),
            withLoading(this.isCheckingDuplicates),
            takeUntil(this.cancelDuplicateCheck$),
            tap((results) => {
                const blockedCorrections = new Map<string, string>();

                results.forEach(({ file, isBlocked, hasConflict, suggestedTitle, finalStem }) => {
                    this.claimedStems.set(file.id, finalStem);

                    if (!hasConflict) return;

                    if (isBlocked) {
                        // Keep BLOCKED status; just feed the pantry a valid rename suggestion
                        blockedCorrections.set(file.id, suggestedTitle);
                    } else {
                        this.resolvedDuplicates.set(file.id, {
                            fileId: file.id,
                            isDuplicate: true,
                            suggestedName: suggestedTitle,
                        });
                    }
                });

                this.duplicateFiles.set([...this.resolvedDuplicates.values()]);

                if (blockedCorrections.size > 0) {
                    this.uploaderComponent()?.selectedFiles.update((current) =>
                        current.map((f) => {
                            const corrected = blockedCorrections.get(f.id);
                            return corrected ? { ...f, suggestedRename: corrected } : f;
                        }),
                    );
                }
            }),
        );
    }

    /**
     * Re-validates a file's name against the backend after the pantry uploader's
     * inline rename flow confirms a rename, so the local `existingLibraryNames`
     * pre-check doesn't have the final say.
     *
     * @param event - The rename details emitted by the pantry uploader.
     */
    protected onFileRenamed(event: RenamedFileEvent): void {
        const file = this.uploaderComponent()
            ?.selectedFiles()
            .find((f) => f.id === event.fileId);
        if (!file) return;

        this.renameCheckSub?.unsubscribe();

        this.renameCheckSub = this.checkNameConflict(event.newName, file.mimeType, event.extension)
            .pipe(withLoading(this.isCheckingDuplicates), takeUntilDestroyed(this.destroyRef))
            .subscribe(({ hasConflict, response }) => {
                const finalStem =
                    hasConflict && response
                        ? this.getComparableStem(response.suggestedFileName, event.extension)
                        : this.getComparableStem(event.newName, event.extension);
                this.claimedStems.set(event.fileId, this.getClaimKey(finalStem, event.extension));

                if (hasConflict && response) {
                    this.resolvedDuplicates.set(event.fileId, {
                        fileId: event.fileId,
                        isDuplicate: true,
                        suggestedName: response.suggestedTitle,
                    });
                } else {
                    this.resolvedDuplicates.delete(event.fileId);
                }
                this.duplicateFiles.set([...this.resolvedDuplicates.values()]);
            });
    }

    /**
     * Applies the default optimize toggle to selected video files so the
     * Video Optimization panel appears immediately after selection.
     *
     * @param fileIds - Optional ids to limit updates to the files from the latest selection event.
     */
    private applyVideoOptimizationDefaults(fileIds: string[]): void {
        const uploader = this.uploaderComponent();
        if (!uploader || fileIds.length === 0) return;

        const targetIds = new Set(fileIds);
        const defaultOptimize = this.defaultOptimizeVideo();

        uploader.selectedFiles.update((current) => {
            let changed = false;
            const next = current.map((f) => {
                if (!targetIds.has(f.id) || getMediaType(f) !== 'video') return f;
                if (this.videoDefaultsApplied.has(f.id)) return f;

                this.videoDefaultsApplied.add(f.id);
                if (f.optimizeVideo === defaultOptimize) return f;

                changed = true;
                return { ...f, optimizeVideo: defaultOptimize };
            });
            return changed ? next : current;
        });
    }

    /**
     * Normalizes a filename to a comparable stem (lowercase, no extension).
     *
     * @param name - Raw filename or title from the API or upload file.
     * @param extension - Optional extension when `name` does not include it.
     * @returns Lowercase stem used for duplicate comparison.
     */
    private getComparableStem(name: string, extension?: string): string {
        let stem = name.trim().toLowerCase();
        if (extension) {
            const suffix = `.${extension.toLowerCase()}`;
            if (stem.endsWith(suffix)) {
                stem = stem.slice(0, -suffix.length);
            }
        }
        const lastDot = stem.lastIndexOf('.');
        return lastDot === -1 ? stem : stem.substring(0, lastDot);
    }

    /**
     * Builds the key used to track a claimed name across a batch. Two files can
     * legitimately share the same stem as long as their extensions differ (e.g.
     * `test_duplicate.png` and `test_duplicate.mp4` are different files, not a
     * collision) — so the claim key includes the extension, unlike the bare stem
     * used for a single file's own conflict comparison.
     */
    private getClaimKey(stem: string, extension?: string): string {
        return extension ? `${stem}.${extension.toLowerCase()}` : stem;
    }

    /**
     * Returns the comparable stem for an upload file.
     *
     * @param file - Selected upload file from the pantry uploader.
     * @returns Normalized stem for duplicate checks.
     */
    private getUploadFileStem(file: UploadFile): string {
        return this.getComparableStem(file.currentName, file.extension);
    }

    /**
     * Cancels the current file upload if one is in progress.
     * Invokes the cancel callback and resets it to undefined afterward
     * to prevent duplicate cancellation attempts.
     */
    protected onCancelUpload(): void {
        this.cancelUpload?.();
        this.cancelUpload = undefined;
    }

    /**
     * Initiates the upload process for the provided files.
     * Sets up a cancellation mechanism that halts any further progress,
     * success, or error updates if the upload is cancelled mid-flight.
     *
     * @param files - The list of files to be uploaded.
     */
    protected onUploadFiles(files: UploadFile[]): void {
        // Flag indicating if the upload has been cancelled
        let cancelled = false;
        let triggerFilestackAbort: (() => void) | undefined;

        // Set the cancel upload callback
        this.onCancelUpload = () => {
            cancelled = true;
            triggerFilestackAbort?.();
        };

        this.filestackService.uploadFiles(
            files,
            this.uploadConfig() ?? getUploaderConfig(this.env),
            (fileId, percent) => {
                if (cancelled) return;
                this.uploaderComponent()?.updateProgress(fileId, percent);
            },
            (fileId, cdnUrl, s3Url, uuid, optimizeVideo) => {
                if (cancelled) return;

                const file = files.find((f) => f.id === fileId);

                /**
                 * Emit the uploaded file/object and
                 * a callback function to trigger when the it has been save to the database really
                 */
                if (file) {
                    const normalizedFileType = this.normalizeFileType(
                        file.mimeType,
                        file.extension,
                    );

                    this.onUploadSuccess.emit({
                        file: {
                            ...file,
                            mimeType: normalizedFileType,
                            url: cdnUrl,
                            s3Url,
                            uuid,
                            optimizeVideo,
                        } as UploadFile & {
                            url: string;
                            s3Url?: string;
                            uuid?: string;
                            optimizeVideo?: boolean;
                        },
                        cb: (fileId, url) => {
                            this.uploaderComponent()?.markFileUploaded(fileId, url);
                            // Refresh the local name cache so a later selection in this
                            // same session knows this filename is now taken.
                            this.loadExistingLibraryNames(this.dealerId());
                        },
                    });
                }
            },
            (fileId, error) => {
                if (cancelled) return;
                this.uploaderComponent()?.markFileError(fileId, error);
            },
            (cancelRegistration) => {
                // Allows us to natively abort backend filestack HTTP requests.
                triggerFilestackAbort = cancelRegistration;
            },
        );
    }
}
